// src/components/admin/dashboard/QuickActionCard.tsx
import { Link } from '@tanstack/react-router';
import { type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface QuickActionCardProps {
  to?: string;
  onClick?: () => void;
  icon: LucideIcon;
  title: string;
  description: string;
}

export function QuickActionCard({
  to,
  onClick,
  icon: Icon,
  title,
  description,
}: QuickActionCardProps) {
  const content = (
    <>
      <div className="bg-primary-50 text-primary-500 group-hover:bg-primary-100 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors">
        <Icon className="h-4.5 w-4.5" strokeWidth={1.5} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="text-body-sm text-accent-ink truncate leading-tight font-semibold">
          {title}
        </div>
        <div className="text-meta-sm mt-0.5 truncate leading-tight text-neutral-500">
          {description}
        </div>
      </div>
    </>
  );

  const sharedClass = cn(
    'group flex h-18 items-center gap-3.5 rounded-xl border border-(--color-border) bg-surface px-4 shadow-xs',
    'transition-all duration-200 ease-out',
    'hover:border-primary-200 hover:shadow-md hover:-translate-y-0.5',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'motion-reduce:transform-none motion-reduce:hover:translate-y-0',
  );

  if (to) {
    return (
      <Link to={to as never} className={sharedClass}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={sharedClass}>
      {content}
    </button>
  );
}
